// Perspective Lenses — React port of kula-create's perspective-lenses.html.
//
// Pedagogy: multi-stakeholder analysis of one stimulus. Learner views the
// scenario through each lens (e.g. environmentalist, economist, parent) and
// answers lens-specific sub-prompts.
//
// Fires onActivityResponse per lens completion:
//   { lensId, answers: { [subPromptId]: value } }

import { useState, useCallback, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import type { PerspectiveLensesSlideData, Theme, ExperienceConclusion } from '../../slideTypes';
import { slideTypographyStyle } from '../../slideTypes';
import { Reveal } from '../../shared/Reveal';
import { ExperienceClose } from '../../shared/ExperienceClose';
import { ExperienceExhibit } from '../../shared/ExperienceExhibit';
import type { ActivityResponsePayload } from '../../../../../types';
import { RisographPerspectiveLenses } from './RisographPerspectiveLenses';

interface Props {
  data: PerspectiveLensesSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: PerspectiveLensesSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}

export function PerspectiveLensesSlide({ data, theme, isEditing, onUpdate, onActivityResponse, onComplete, savedResponse }: Props) {
  if (isEditing) return <BasicEditor data={data} theme={theme} onUpdate={onUpdate} />;
  // Tier-A bespoke renderer for the "Risograph Assembly" theme world (flagship pairing).
  if (theme.id === 'risograph') {
    return <RisographPerspectiveLenses data={data} theme={theme} onActivityResponse={onActivityResponse} onComplete={onComplete} savedResponse={savedResponse} />;
  }
  return (
    <PerspectiveLensesStudent
      data={data}
      theme={theme}
      onActivityResponse={onActivityResponse}
      onComplete={onComplete}
      savedResponse={savedResponse}
    />
  );
}

function PerspectiveLensesStudent({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: PerspectiveLensesSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const accent = theme.colors.accent || theme.colors.primary;
  // Only the last response payload is persisted; a per-lens payload can't
  // reconstruct mid-activity progress, so only a saved synthesis restores
  // (as fully done) — anything else starts fresh.
  const restoredDone = savedResponse?.lensId === 'synthesis';
  const [activeLensId, setActiveLensId] = useState<string | null>(restoredDone ? null : data.lenses[0]?.id ?? null);
  const [responses, setResponses] = useState<Record<string, Record<string, string>>>({});
  const [completedLenses, setCompletedLenses] = useState<Set<string>>(() =>
    restoredDone ? new Set(data.lenses.map((l) => l.id)) : new Set(),
  );
  const [synthesis, setSynthesis] = useState(() => {
    const answers =
      restoredDone && savedResponse && typeof savedResponse.answers === 'object' && savedResponse.answers !== null
        ? (savedResponse.answers as Record<string, unknown>)
        : null;
    return typeof answers?.text === 'string' ? answers.text : '';
  });
  const [synthesisSubmitted, setSynthesisSubmitted] = useState(restoredDone);
  const completedRef = useRef(restoredDone);

  const allDone = completedLenses.size === data.lenses.length && data.lenses.length > 0;
  // Fully done = lenses locked AND (synthesis submitted, or no synthesis authored).
  const fullyDone = allDone && (!data.synthesis?.prompt || synthesisSubmitted);

  const markComplete = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete?.();
  }, [onComplete]);

  const updateAnswer = (lensId: string, promptId: string, value: string) =>
    setResponses((prev) => ({
      ...prev,
      [lensId]: { ...(prev[lensId] ?? {}), [promptId]: value },
    }));

  const isLensReady = (lensId: string) => {
    const lens = data.lenses.find((l) => l.id === lensId);
    if (!lens) return false;
    const answers = responses[lensId] ?? {};
    return lens.subPrompts.every((p) => (answers[p.id] ?? '').trim().length > 0);
  };

  const completeLens = useCallback(
    (lensId: string) => {
      const answers = responses[lensId] ?? {};
      onActivityResponse?.({
        activityType: 'perspective-lenses',
        mode: 'survey',
        responseData: { lensId, answers },
      });
      const nextDone = new Set(completedLenses).add(lensId);
      setCompletedLenses(nextDone);
      // No synthesis prompt authored — finishing the lenses is completion.
      if (nextDone.size === data.lenses.length && !data.synthesis?.prompt) markComplete();
      // Advance to the next un-done lens AFTER this one, wrapping around.
      const idx = data.lenses.findIndex((l) => l.id === lensId);
      for (let step = 1; step <= data.lenses.length; step++) {
        const candidate = data.lenses[(idx + step) % data.lenses.length];
        if (!nextDone.has(candidate.id)) {
          setActiveLensId(candidate.id);
          break;
        }
      }
    },
    [responses, onActivityResponse, data.lenses, data.synthesis, completedLenses, markComplete],
  );

  const submitSynthesis = useCallback(() => {
    if (synthesis.trim().length === 0) return;
    setSynthesisSubmitted(true);
    // responseToActivityKey reads lensId, so this lands as its own keyed row.
    onActivityResponse?.({
      activityType: 'perspective-lenses',
      mode: 'survey',
      responseData: { lensId: 'synthesis', answers: { text: synthesis } },
    });
    markComplete();
  }, [synthesis, onActivityResponse, markComplete]);

  const activeLens = data.lenses.find((l) => l.id === activeLensId);

  // Close: the learner's read from each lens, shown back together (one echo per
  // lens, values joined). Free-text by nature, so it echoes rather than reflects
  // a number. Labels avoid dashes (echo labels are not run through normalizeProse).
  const lensEchoes = data.lenses.map((lens) => ({
    label: lens.label,
    value: lens.subPrompts
      .map((p) => responses[lens.id]?.[p.id])
      .filter((v): v is string => Boolean(v && v.trim()))
      .join(' · '),
  }));
  const lensConclusion: ExperienceConclusion = data.conclusion ?? {
    heading: 'Through every lens',
    body: 'Here is how each lens read the same situation.',
  };

  return (
    <div
      className="h-full w-full overflow-y-auto"
      style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}
    >
      <div className="mx-auto max-w-[820px] px-6 py-10">
        <header className="text-center mb-6">
          <h1 style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text, marginBottom: 8 }}>
            {data.title}
          </h1>
          {data.subtitle && <p style={{ color: theme.colors.textMuted, fontSize: '0.95rem' }}>{data.subtitle}</p>}
        </header>

        {/* Stimulus card */}
        <div
          className="rounded-lg p-5 mb-6 border-l-4"
          style={{ background: theme.colors.surface, borderColor: accent }}
        >
          <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: accent }}>
            The scenario
          </p>
          <p className="leading-relaxed text-sm" style={{ color: theme.colors.text }}>
            {data.stimulus.context}
          </p>
        </div>

        {/* The exhibit — the artifact every lens re-reads, shown in full so no
            lens question refers to something that isn't on screen. */}
        {data.exhibit && <ExperienceExhibit exhibit={data.exhibit} theme={theme} />}

        {/* Lens tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {data.lenses.map((lens) => {
            const isActive = lens.id === activeLensId;
            const isDone = completedLenses.has(lens.id);
            return (
              <button
                key={lens.id}
                onClick={() => setActiveLensId(lens.id)}
                className="px-3 py-1.5 rounded-full text-xs font-semibold"
                style={{
                  background: isActive ? (lens.colour || accent) : isDone ? `${lens.colour || accent}25` : theme.colors.surface,
                  color: isActive ? '#fff' : isDone ? (lens.colour || accent) : theme.colors.textMuted,
                  border: `1px solid ${isActive ? (lens.colour || accent) : 'transparent'}`,
                }}
              >
                {lens.label}
                {isDone && ' ✓'}
              </button>
            );
          })}
        </div>

        {/* Active lens panel — keyed by lens id so switching lenses remounts and
            fades the new lens in ("putting on a different lens"). No
            <AnimatePresence mode="wait">: Reveal is a plain (non-forwardRef)
            component, so the exiting lens would block the next from mounting and
            freeze the panel on an invisible card. */}
        {activeLens && (
            <Reveal
              key={activeLensId}
              variant="fade"
              className="rounded-lg p-5 mb-6 border-2"
              style={{
                background: theme.colors.surface,
                borderColor: `${activeLens.colour || accent}50`,
              }}
            >
              <p className="text-base font-semibold mb-4" style={{ color: theme.colors.text }}>
                {activeLens.prompt}
              </p>
              <div className="space-y-4">
                {activeLens.subPrompts.map((p) => (
                  <div key={p.id}>
                    <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text }}>
                      {p.text}
                    </label>
                    {/* A select with missing/empty options is un-answerable — fall back to text. */}
                    {p.inputType === 'select' && (p.options?.length ?? 0) > 0 ? (
                      <select
                        value={responses[activeLens.id]?.[p.id] ?? ''}
                        disabled={completedLenses.has(activeLens.id)}
                        onChange={(e) => updateAnswer(activeLens.id, p.id, e.target.value)}
                        className="w-full px-3 py-2 rounded border text-sm"
                        style={{
                          background: theme.colors.background,
                          borderColor: `${theme.colors.textMuted}33`,
                          color: theme.colors.text,
                        }}
                      >
                        <option value="">Select…</option>
                        {(p.options ?? []).map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : (
                      <textarea
                        value={responses[activeLens.id]?.[p.id] ?? ''}
                        disabled={completedLenses.has(activeLens.id)}
                        onChange={(e) => updateAnswer(activeLens.id, p.id, e.target.value)}
                        maxLength={p.maxLength}
                        rows={2}
                        className="w-full px-3 py-2 rounded border text-sm"
                        style={{
                          background: theme.colors.background,
                          borderColor: `${theme.colors.textMuted}33`,
                          color: theme.colors.text,
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
              {!completedLenses.has(activeLens.id) && (
                <button
                  onClick={() => completeLens(activeLens.id)}
                  disabled={!isLensReady(activeLens.id)}
                  className="mt-4 w-full px-6 py-3 rounded-lg font-semibold text-sm"
                  style={{
                    background: activeLens.colour || accent,
                    color: '#fff',
                    cursor: isLensReady(activeLens.id) ? 'pointer' : 'not-allowed',
                    opacity: isLensReady(activeLens.id) ? 1 : 0.4,
                  }}
                >
                  Lock this lens
                </button>
              )}
            </Reveal>
          )}

        {/* Synthesis — payoff register: all lenses locked, the views resolve into one */}
        {allDone && data.synthesis?.prompt && (
          <Reveal
            variant="scale"
            spring="bouncy"
            className="rounded-2xl p-5 border-2 shadow-sm"
            style={{ background: theme.colors.surface, borderColor: accent }}
          >
            <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: accent }}>
              Synthesise
            </p>
            <p className="text-sm mb-3" style={{ color: theme.colors.text }}>{data.synthesis.prompt}</p>

            {/* Side-by-side lens comparison before synthesising */}
            {data.synthesis.showLensComparison && !synthesisSubmitted && (
              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                {data.lenses.map((lens) => (
                  <div
                    key={lens.id}
                    className="rounded p-3 border"
                    style={{ background: theme.colors.background, borderColor: `${lens.colour || accent}40` }}
                  >
                    <p className="text-xs font-bold mb-2" style={{ color: lens.colour || accent }}>{lens.label}</p>
                    {lens.subPrompts.map((p) => {
                      const answer = responses[lens.id]?.[p.id];
                      return answer ? (
                        <p key={p.id} className="text-xs mb-1" style={{ color: theme.colors.text }}>
                          <span style={{ color: theme.colors.textMuted }}>{p.text}</span> {answer}
                        </p>
                      ) : null;
                    })}
                  </div>
                ))}
              </div>
            )}

            {synthesisSubmitted ? (
              <>
                {synthesis && (
                  <p className="text-sm italic mb-3" style={{ color: theme.colors.text }}>{synthesis}</p>
                )}
                <p className="text-sm font-semibold" style={{ color: '#16a34a' }}>
                  ✓ Synthesis recorded — activity complete.
                </p>
              </>
            ) : (
              <>
                <textarea
                  value={synthesis}
                  onChange={(e) => setSynthesis(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 rounded border text-sm"
                  style={{
                    background: theme.colors.background,
                    borderColor: `${theme.colors.textMuted}33`,
                    color: theme.colors.text,
                  }}
                />
                <button
                  onClick={submitSynthesis}
                  disabled={synthesis.trim().length === 0}
                  className="mt-3 w-full px-6 py-3 rounded-lg font-semibold text-sm"
                  style={{
                    background: accent,
                    color: '#fff',
                    cursor: synthesis.trim().length > 0 ? 'pointer' : 'not-allowed',
                    opacity: synthesis.trim().length > 0 ? 1 : 0.4,
                  }}
                >
                  Submit synthesis
                </button>
              </>
            )}
          </Reveal>
        )}

        {/* Authored close — surfaces the learner's reads from every lens side by
            side (the review's showLensComparison payoff: their writing no longer
            vanishes). Echoes one read per lens. Shown once the activity is fully
            done (all lenses + synthesis, or all lenses when no synthesis authored). */}
        <AnimatePresence>
          {fullyDone && (
            <Reveal variant="up" className="mt-4">
              <ExperienceClose conclusion={lensConclusion} theme={theme} echoes={lensEchoes} />
            </Reveal>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ---- Editor (minimal — top-level fields only; structural edits via AI) ----

function BasicEditor({
  data,
  theme,
  onUpdate,
}: {
  data: PerspectiveLensesSlideData;
  theme: Theme;
  onUpdate: (data: PerspectiveLensesSlideData) => void;
}) {
  return (
    <div className="h-full w-full overflow-y-auto p-8">
      <div className="mx-auto max-w-[720px] space-y-3">
        <input
          value={data.title}
          onChange={(e) => onUpdate({ ...data, title: e.target.value })}
          placeholder="Title…"
          className="w-full bg-transparent border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none"
          style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}
        />
        <input
          value={data.subtitle}
          onChange={(e) => onUpdate({ ...data, subtitle: e.target.value })}
          placeholder="Subtitle"
          className="w-full bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none text-sm"
          style={{ color: theme.colors.textMuted }}
        />
        <textarea
          value={data.stimulus.context}
          onChange={(e) => onUpdate({ ...data, stimulus: { ...data.stimulus, context: e.target.value } })}
          placeholder="Stimulus context (80-150 words)"
          rows={5}
          className="w-full px-3 py-2 rounded border text-sm"
          style={{
            background: theme.colors.surface,
            borderColor: `${theme.colors.textMuted}33`,
            color: theme.colors.text,
          }}
        />
        <textarea
          value={data.synthesis?.prompt ?? ''}
          onChange={(e) => onUpdate({ ...data, synthesis: { ...data.synthesis, prompt: e.target.value } })}
          placeholder="Synthesis prompt"
          rows={2}
          className="w-full px-3 py-2 rounded border text-sm"
          style={{
            background: theme.colors.surface,
            borderColor: `${theme.colors.textMuted}33`,
            color: theme.colors.text,
          }}
        />
        <div className="rounded p-3 text-xs" style={{ background: `${theme.colors.textMuted}11`, color: theme.colors.textMuted }}>
          {data.lenses.length} lens{data.lenses.length === 1 ? '' : 'es'} · {data.lenses.reduce((n, l) => n + l.subPrompts.length, 0)} sub-prompts total.
          Structural changes (lens count, sub-prompts) are AI-only — open the AI build panel to regenerate.
        </div>
      </div>
    </div>
  );
}
